import React, {useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo, useCallback} from 'react';
import { useMsal } from '@azure/msal-react';
import {Player, PlayerEvent} from 'bitmovin-player';
import { UIFactory } from 'bitmovin-player/bitmovinplayer-ui';
import 'bitmovin-player/bitmovinplayer-ui.css';
import configData from "../../../../../config.json";

const BitmovinPlayer = forwardRef(({content, onEnded, onPause, currentTimeStamp}, ref) => {

  // Override console.error to suppress isCasting errors
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const message = args[0];
      if (typeof message === 'string' && message.includes('isCasting')) {
        return; // Suppress isCasting errors
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      // Restore original console.error on cleanup
      console.error = originalConsoleError;
    };
  }, []);

  const [player, setPlayer] = useState(null);
  const [playerSource, setPlayerSource] = useState(null);
  const [isPlaybackVisible, setIsPlaybackVisible] = useState(false);
  const [sourceError, setSourceError] = useState(false);
  const [showError, setShowError] = useState(false);

  const { instance, accounts } = useMsal();

  const playerDiv = useRef();
  const playerRef = useRef(null);
  const keepaliveIntervalRef = useRef(null);
  const initialSeekRef = useRef(currentTimeStamp);
  initialSeekRef.current = currentTimeStamp;

  const playerConfig = useMemo(() => ({
    key: configData.BITMOVIN_PLAYER_KEY,
    ui: false,
    playback: {autoplay: true}
  }), []);

  const handleOnEnded = useCallback(() => {
    if (!onEnded) return;
    onEnded();
  }, [onEnded]);

  const handleOnPause = useCallback(() => {
    const currentPlayer = playerRef.current;
    if (currentPlayer) {
      try {
        onPause(currentPlayer.getCurrentTime());
      } catch (error) {
        console.warn('Player getCurrentTime error in handleOnPause:', error.message);
      }
    }
  }, [onPause]);

  useEffect(() => {
    if (content) {
      setIsPlaybackVisible(false);
      setSourceError(false);
      
      // Validate source before setting it
      const hasValidStream = content.DashUrl || content.HlsUrl;
      if (!hasValidStream) {
        console.warn('Invalid video source: No Dash or Hls URL provided', content);
        setSourceError(true);
        setPlayerSource(null);
        return;
      }
      
      setPlayerSource(() => ({
        dash: content.DashUrl,
        hls:  content.HlsUrl, 
        poster: content.thumbUrl
      }));
    }
  }, [content]);

  useEffect(() => {
    const currentPlayer = playerRef.current;
    if(currentPlayer) {
      try {
        currentPlayer.on(PlayerEvent.Paused, handleOnPause);
        currentPlayer.on(PlayerEvent.PlaybackFinished, handleOnEnded);
      } catch (error) {
        console.warn('Player event listener setup error:', error.message);
        return;
      }

      return () => {
        try {
          currentPlayer.off(PlayerEvent.Paused, handleOnPause);
          currentPlayer.off(PlayerEvent.PlaybackFinished, handleOnEnded);
        } catch (error) {
          // Player already destroyed or in invalid state
          console.warn('Player event cleanup error (already destroyed):', error.message);
        }
      };
    }
  }, [player, handleOnPause, handleOnEnded]);

  useEffect(() => {
    const currentPlayer = playerRef.current;
    if (!currentPlayer) return;

    const KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // Fire token check every 4 minutes, since MSAL checks token validity and only gets new token when clsoe to expiration.

    const doKeepalive = async () => {
      try {
        await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [
            `https://${configData.AUTH.ADB2C_TENANT_NAME}.onmicrosoft.com/core-api/core-api.access`,
          ],
        });
      } catch (err) {
        console.log('[keepalive] acquireTokenSilent error (swallowed):', err?.message);
      }
    };

    const handlePlaying = () => {
      clearInterval(keepaliveIntervalRef.current);
      doKeepalive();
      keepaliveIntervalRef.current = setInterval(doKeepalive, KEEPALIVE_INTERVAL_MS);
    };

    const handleStop = () => {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    };

    try {
      currentPlayer.on(PlayerEvent.Playing, handlePlaying);
      currentPlayer.on(PlayerEvent.Paused, handleStop);
      currentPlayer.on(PlayerEvent.PlaybackFinished, handleStop);
    } catch (error) {
      console.warn('Keepalive event listener setup error:', error.message);
      return;
    }

    return () => {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
      try {
        currentPlayer.off(PlayerEvent.Playing, handlePlaying);
        currentPlayer.off(PlayerEvent.Paused, handleStop);
        currentPlayer.off(PlayerEvent.PlaybackFinished, handleStop);
      } catch {
        // Player already destroyed
      }
    };
  }, [player, instance, accounts]);

  useEffect(() => {
    async function setupPlayer() {
      if (!playerSource) return;

      setIsPlaybackVisible(false);
      const playerInstance = new Player(playerDiv.current, playerConfig);
      UIFactory.buildDefaultUI(playerInstance);

      const handlePlaybackVisible = () => {
        setIsPlaybackVisible(true);
      };

      try {
        playerInstance.on(PlayerEvent.Playing, handlePlaybackVisible);
      } catch (error) {
        console.warn('Player visibility event setup error:', error.message);
      }
      
      try {
        await playerInstance.load(playerSource);
        playerInstance.seek(initialSeekRef.current);
        setPlayer(playerInstance);
        playerRef.current = playerInstance;
      } catch (error) {
        console.error('Error loading player source:', error);
        setSourceError(true);
        
        // Handle specific error codes
        if (error.code === 1201) { // SOURCE_INVALID
          console.warn('Source invalid - no usable streams found');
        }
      }
    }

    setupPlayer();

    return () => {
      const currentPlayer = playerRef.current;
      if (currentPlayer != null) {
        setPlayer(null);
        playerRef.current = null;
        try {
          currentPlayer.destroy();
        } catch (error) {
          console.warn('Player destroy error (already destroyed):', error.message);
        }
      }
    };
  }, [playerSource, playerConfig]);

  // Handle 3-second delay before showing error
  useEffect(() => {
    if (sourceError) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 10000);
      
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [sourceError]);

  // Expose player methods through ref
  useImperativeHandle(ref, () => ({
    pause: () => {
      const currentPlayer = playerRef.current;
      if (currentPlayer) {
        try {
          if (currentPlayer.isPlaying()) {
            currentPlayer.pause();
            onPause(currentPlayer.getCurrentTime());
          }
        } catch (error) {
          console.warn('Player pause error:', error.message);
        }
      }
    },
    getCurrentTime: () => {
      const currentPlayer = playerRef.current;
      if (currentPlayer) {
        try {
          return currentPlayer.getCurrentTime();
        } catch (error) {
          console.warn('Player getCurrentTime error:', error.message);
          return 0;
        }
      }
      return 0;
    },
    getPlayer: () => playerRef.current
  }));

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!isPlaybackVisible && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            zIndex: 1
          }}
        >
          {sourceError && showError ? (
            <div style={{ textAlign: 'center', color: '#666' }}>
              <div>Unable to load video</div>
              <div style={{ fontSize: '0.9em', marginTop: '0.5em' }}>
                The video source is invalid or unavailable
              </div>
            </div>
          ) : (
            'Loading video...'
          )}
        </div>
      )}
      <div
        id='player'
        ref={playerDiv}
        style={{ visibility: isPlaybackVisible ? 'visible' : 'hidden' }}
      />
    </div>
  );
});

export default BitmovinPlayer;